import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { useSortable } from "../lib/useSortable";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, X, Save, ListChecks, Hash, ChevronDown, ChevronRight } from "lucide-react";

const GROUP_LABELS = {
    fib: "FIB",
    whr: "WHR",
    global: "Geral",
    custom: "Personalizadas",
};
const QTYPES = [
    { v: "scale_1_5", label: "Escala 1-5" },
    { v: "single_choice", label: "Escolha única" },
    { v: "multi_choice", label: "Escolha múltipla" },
    { v: "text", label: "Texto livre" },
    { v: "number", label: "Número" },
];

export default function AdminHappinessPage() {
    const [tab, setTab] = useState("indices");
    return (
        <div className="space-y-8" data-testid="admin-happiness-page">
            <div>
                <h1 className="font-display text-5xl text-[#0d1628]">Admin · Inquéritos de Felicidade</h1>
                <p className="text-[#52525b] mt-3 max-w-3xl">
                    Gerir os índices FIB, WHR e personalizados, bem como as perguntas associadas a cada fase da metodologia.
                </p>
            </div>
            <div className="flex gap-2 border-b border-[#e4e4e7]">
                {[
                    { v: "indices", label: "Índices", icon: ListChecks },
                    { v: "questions", label: "Perguntas", icon: Hash },
                ].map((t) => (
                    <button key={t.v} type="button" onClick={() => setTab(t.v)}
                        data-testid={`tab-${t.v}`}
                        className={`px-4 py-3 text-sm flex items-center gap-2 border-b-2 -mb-px transition-colors ${tab === t.v ? "border-[#b89b6f] text-[#0d1628]" : "border-transparent text-[#52525b] hover:text-[#0d1628]"}`}>
                        <t.icon size={14} /> {t.label}
                    </button>
                ))}
            </div>
            {tab === "indices" ? <IndicesTab /> : <QuestionsTab />}
        </div>
    );
}

function SortableTh({ label, k, sortKey, sortDir, onSort, className = "text-left p-3" }) {
    const active = sortKey === k;
    const arrow = active ? (sortDir === "asc" ? "↑" : "↓") : "↕";
    return (
        <th className={className} data-testid={`sort-${k}`}>
            <button type="button" onClick={() => onSort(k)}
                className={`label-caps inline-flex items-center gap-1 ${active ? "text-[#0d1628]" : "text-[#52525b] hover:text-[#0d1628]"}`}>
                {label}
                <span className={`text-[10px] ${active ? "text-[#b89b6f]" : "text-[#a1a1aa]"}`}>{arrow}</span>
            </button>
        </th>
    );
}

function IndicesTab() {
    const [items, setItems] = useState([]);
    const [busy, setBusy] = useState(false);
    const [editing, setEditing] = useState(null);
    const [expanded, setExpanded] = useState(null);
    const [expandedData, setExpandedData] = useState({}); // {idx_id: {questions, total, loading}}

    const load = () => api.get("/happiness/indices").then((r) => setItems(r.data || []))
        .catch((e) => toast.error(formatApiError(e)));
    useEffect(() => { load(); }, []);

    const accessors = useMemo(() => ({
        owner_scope: (r) => r.owner_scope === "super_admin" ? "global" : "empresa",
        active: (r) => r.active ? 1 : 0,
    }), []);
    const { items: sortedItems, sortKey, sortDir, requestSort } = useSortable(items, "name", "asc", accessors);

    const toggleExpand = async (idxId) => {
        if (expanded === idxId) {
            setExpanded(null);
            return;
        }
        setExpanded(idxId);
        if (!expandedData[idxId]) {
            setExpandedData((prev) => ({ ...prev, [idxId]: { loading: true } }));
            try {
                const r = await api.get(`/happiness/indices/${idxId}/questions`);
                setExpandedData((prev) => ({ ...prev, [idxId]: { ...r.data, loading: false } }));
            } catch (e) {
                toast.error(formatApiError(e));
                setExpandedData((prev) => ({ ...prev, [idxId]: { loading: false, questions: [], total: 0 } }));
            }
        }
    };

    const submit = async (payload) => {
        setBusy(true);
        try {
            if (editing?.id && editing._isExisting) {
                const upd = { name: payload.name, description: payload.description, group: payload.group, active: payload.active };
                await api.patch(`/admin/happiness/indices/${editing.id}`, upd);
                toast.success("Índice atualizado");
            } else {
                await api.post("/admin/happiness/indices", payload);
                toast.success("Índice criado");
            }
            setEditing(null);
            load();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    };

    return (
        <div className="space-y-4" data-testid="indices-tab">
            <div className="flex justify-end">
                <button type="button" onClick={() => setEditing({ id: "", name: "", description: "", group: "custom", mandatory_global: false, active: true })}
                    className="btn-primary" data-testid="new-index-btn">
                    <Plus size={14} /> Novo índice
                </button>
            </div>

            {editing && (
                <IndexForm initial={editing} onCancel={() => setEditing(null)} onSubmit={submit} busy={busy} />
            )}

            <div className="border border-[#e4e4e7] overflow-x-auto">
                <table className="w-full text-sm min-w-[640px]">
                    <thead className="bg-[#f5f3ee] text-[#52525b]">
                        <tr>
                            <th className="w-10"></th>
                            <SortableTh label="ID" k="id" sortKey={sortKey} sortDir={sortDir} onSort={requestSort} />
                            <SortableTh label="Nome" k="name" sortKey={sortKey} sortDir={sortDir} onSort={requestSort} />
                            <SortableTh label="Grupo" k="group" sortKey={sortKey} sortDir={sortDir} onSort={requestSort} />
                            <SortableTh label="Tipo" k="owner_scope" sortKey={sortKey} sortDir={sortDir} onSort={requestSort} />
                            <SortableTh label="Estado" k="active" sortKey={sortKey} sortDir={sortDir} onSort={requestSort} />
                            <th className="p-3 w-10"></th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-[#e4e4e7]">
                        {sortedItems.map((i) => {
                            const isOpen = expanded === i.id;
                            const ed = expandedData[i.id];
                            return (
                                <React.Fragment key={i.id}>
                                    <tr data-testid={`index-row-${i.id}`} className="hover:bg-[#fdfcf9] cursor-pointer" onClick={() => toggleExpand(i.id)}>
                                        <td className="p-3 text-[#52525b]" data-testid={`index-expand-${i.id}`}>
                                            {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                                        </td>
                                        <td className="p-3 font-mono text-xs text-[#52525b]">{i.id}</td>
                                        <td className="p-3 text-[#0d1628]">{i.name}</td>
                                        <td className="p-3"><span className="text-xs bg-[#f5f3ee] px-2 py-1">{GROUP_LABELS[i.group] || i.group}</span></td>
                                        <td className="p-3 text-xs text-[#52525b]">{i.owner_scope === "super_admin" ? "Global" : "Empresa"}</td>
                                        <td className="p-3 text-xs">{i.active ? <span className="text-[#16a34a]">Ativo</span> : <span className="text-[#52525b]">Inativo</span>}</td>
                                        <td className="p-3 text-right" onClick={(e) => e.stopPropagation()}>
                                            {i.owner_scope === "super_admin" && (
                                                <button type="button" onClick={() => setEditing({ ...i, _isExisting: true })}
                                                    className="text-[#52525b] hover:text-[#0d1628]" data-testid={`edit-index-${i.id}`}>
                                                    <Pencil size={14} />
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                    {isOpen && (
                                        <tr data-testid={`index-questions-${i.id}`}>
                                            <td colSpan={7} className="bg-[#fdfcf9] border-t border-[#e4e4e7] p-0">
                                                <IndexQuestionsList data={ed} />
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            );
                        })}
                        {sortedItems.length === 0 && (
                            <tr><td colSpan={7} className="p-6 text-center text-[#52525b]">Sem índices.</td></tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function IndexQuestionsList({ data }) {
    if (!data || data.loading) {
        return <div className="p-5 text-sm text-[#52525b]" data-testid="index-questions-loading">A carregar perguntas...</div>;
    }
    const questions = data.questions || [];
    if (questions.length === 0) {
        return <div className="p-5 text-sm text-[#52525b]" data-testid="index-questions-empty">Nenhuma pergunta associada a este índice.</div>;
    }
    // Group by phase
    const byPhase = {};
    for (const q of questions) (byPhase[q.phase] ||= []).push(q);
    const PHASE_NAMES = { 1: "Avaliar Diagnóstico", 2: "Projetar Futuro", 3: "Planear", 4: "Implementar", 5: "Avaliar Final" };
    return (
        <div className="p-5 space-y-4">
            <div className="label-caps text-[#b89b6f]">{questions.length} pergunta(s) associadas</div>
            {Object.keys(byPhase).sort().map((phase) => (
                <div key={phase}>
                    <div className="flex items-center gap-2 mb-2">
                        <span className="inline-flex items-center justify-center w-6 h-6 bg-[#0d1628] text-white font-display text-xs">{phase}</span>
                        <span className="text-sm text-[#0d1628] font-medium">Fase {phase} · {PHASE_NAMES[phase]}</span>
                    </div>
                    <ul className="space-y-1.5 ml-8">
                        {byPhase[phase].map((q) => (
                            <li key={q.id} className="text-sm text-[#0d1628] flex items-start gap-2" data-testid={`index-q-${q.id}`}>
                                <span className="text-[#b89b6f] font-display text-base flex-shrink-0">{q.order}.</span>
                                <div className="flex-1">
                                    <span>{q.text}{q.mandatory && <span className="text-[#dc2626] ml-1">*</span>}</span>
                                    <span className="ml-2 text-xs text-[#52525b]">[{q.type}{q.scope === "collective" ? ", coletivo" : ""}]</span>
                                </div>
                            </li>
                        ))}
                    </ul>
                </div>
            ))}
        </div>
    );
}

function IndexForm({ initial, onCancel, onSubmit, busy }) {
    const [f, setF] = useState({ ...initial });
    const isExisting = initial._isExisting;
    return (
        <form onSubmit={(e) => { e.preventDefault(); onSubmit(f); }} className="border border-[#e4e4e7] p-5 bg-[#fdfcf9] space-y-3" data-testid="index-form">
            <div className="flex items-center justify-between">
                <div className="label-caps">{isExisting ? "Editar índice" : "Novo índice"}</div>
                <button type="button" onClick={onCancel} className="text-[#52525b]"><X size={16} /></button>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
                <div>
                    <label className="label-caps text-xs">ID*</label>
                    <input className="input-clean" value={f.id} onChange={(e) => setF({ ...f, id: e.target.value.replace(/[^a-z0-9_]/g, "_") })}
                        required disabled={isExisting} data-testid="index-id-input" />
                </div>
                <div>
                    <label className="label-caps text-xs">Nome*</label>
                    <input className="input-clean" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} required data-testid="index-name-input" />
                </div>
                <div className="sm:col-span-2">
                    <label className="label-caps text-xs">Descrição</label>
                    <input className="input-clean" value={f.description || ""} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="index-desc-input" />
                </div>
                <div>
                    <label className="label-caps text-xs">Grupo</label>
                    <select className="input-clean" value={f.group} onChange={(e) => setF({ ...f, group: e.target.value })} data-testid="index-group-input">
                        <option value="fib">FIB</option>
                        <option value="whr">WHR</option>
                        <option value="custom">Personalizado</option>
                        <option value="global">Geral</option>
                    </select>
                </div>
                <div className="flex items-end gap-3">
                    <label className="flex items-center gap-2 text-sm text-[#52525b]">
                        <input type="checkbox" checked={!!f.active} onChange={(e) => setF({ ...f, active: e.target.checked })} data-testid="index-active-input" /> Ativo
                    </label>
                </div>
            </div>
            <div className="flex justify-end gap-2">
                <button type="button" onClick={onCancel} className="btn-outline">Cancelar</button>
                <button type="submit" disabled={busy} className="btn-primary" data-testid="index-save-btn">
                    <Save size={14} /> {busy ? "A guardar..." : "Guardar"}
                </button>
            </div>
        </form>
    );
}

function QuestionsTab() {
    const [phase, setPhase] = useState(1);
    const [items, setItems] = useState([]);
    const [indices, setIndices] = useState([]);
    const [editing, setEditing] = useState(null);
    const [busy, setBusy] = useState(false);

    const load = () => {
        api.get(`/happiness/phases/${phase}/questions`).then((r) => setItems(r.data || []))
            .catch((e) => toast.error(formatApiError(e)));
        api.get(`/happiness/indices`).then((r) => setIndices(r.data || [])).catch(() => { /* indices are optional for this dropdown */ });
    };
    useEffect(() => { load(); }, [phase]);

    const { items: sortedItems, sortKey, sortDir, requestSort } = useSortable(items, "order", "asc");

    const submit = async (payload) => {
        setBusy(true);
        try {
            if (editing?.id && editing._isExisting) {
                const { phase: _p, ...rest } = payload;
                await api.patch(`/happiness/questions/${editing.id}`, rest);
                toast.success("Pergunta atualizada");
            } else {
                await api.post("/admin/happiness/questions", { ...payload, phase });
                toast.success("Pergunta criada");
            }
            setEditing(null);
            load();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    };

    const remove = async (qid) => {
        if (!window.confirm("Eliminar esta pergunta?")) return;
        try {
            await api.delete(`/happiness/questions/${qid}`);
            toast.success("Pergunta eliminada");
            load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    return (
        <div className="space-y-4" data-testid="questions-tab">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-2">
                    <span className="label-caps text-[#52525b]">Fase</span>
                    {[1, 2, 3, 4, 5].map((p) => (
                        <button key={p} type="button" onClick={() => setPhase(p)}
                            className={`px-3 py-1.5 text-sm border ${phase === p ? "bg-[#0d1628] text-white border-[#0d1628]" : "bg-white text-[#52525b] border-[#e4e4e7]"}`}
                            data-testid={`phase-tab-${p}`}>{p}</button>
                    ))}
                </div>
                <button type="button" onClick={() => setEditing({
                    text: "", type: "scale_1_5", indices: [], scope: "individual", mandatory: true,
                    options: [], group: "fib", order: (items.length + 1) * 10, active: true,
                })} className="btn-primary" data-testid="new-question-btn">
                    <Plus size={14} /> Nova pergunta
                </button>
            </div>

            {editing && (
                <QuestionForm initial={editing} indices={indices} onCancel={() => setEditing(null)} onSubmit={submit} busy={busy} />
            )}

            <div className="border border-[#e4e4e7] overflow-x-auto">
                <table className="w-full text-sm min-w-[640px]">
                    <thead className="bg-[#f5f3ee] text-[#52525b]">
                        <tr>
                            <SortableTh label="#" k="order" sortKey={sortKey} sortDir={sortDir} onSort={requestSort} className="text-left p-3 w-12" />
                            <SortableTh label="Pergunta" k="text" sortKey={sortKey} sortDir={sortDir} onSort={requestSort} />
                            <SortableTh label="Tipo" k="type" sortKey={sortKey} sortDir={sortDir} onSort={requestSort} />
                            <SortableTh label="Grupo" k="group" sortKey={sortKey} sortDir={sortDir} onSort={requestSort} />
                            <SortableTh label="Escopo" k="scope" sortKey={sortKey} sortDir={sortDir} onSort={requestSort} />
                            <th className="p-3 w-20"></th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-[#e4e4e7]">
                        {sortedItems.map((q) => (
                            <tr key={q.id} data-testid={`question-row-${q.id}`}>
                                <td className="p-3 text-[#b89b6f] font-display">{q.order}</td>
                                <td className="p-3 text-[#0d1628]">{q.text}{q.mandatory && <span className="text-[#dc2626] ml-1">*</span>}</td>
                                <td className="p-3 text-xs text-[#52525b]">{QTYPES.find((x) => x.v === q.type)?.label || q.type}</td>
                                <td className="p-3 text-xs"><span className="bg-[#f5f3ee] px-2 py-1">{GROUP_LABELS[q.group] || q.group}</span></td>
                                <td className="p-3 text-xs text-[#52525b]">{q.scope === "collective" ? "Coletivo" : "Individual"}</td>
                                <td className="p-3 text-right whitespace-nowrap">
                                    <button type="button" onClick={() => setEditing({ ...q, _isExisting: true })}
                                        className="text-[#52525b] hover:text-[#0d1628] mr-3" data-testid={`edit-q-${q.id}`}>
                                        <Pencil size={14} />
                                    </button>
                                    <button type="button" onClick={() => remove(q.id)}
                                        className="text-[#52525b] hover:text-[#dc2626]" data-testid={`del-q-${q.id}`}>
                                        <Trash2 size={14} />
                                    </button>
                                </td>
                            </tr>
                        ))}
                        {sortedItems.length === 0 && (
                            <tr><td colSpan={6} className="p-6 text-center text-[#52525b]">Sem perguntas nesta fase.</td></tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function QuestionForm({ initial, indices, onCancel, onSubmit, busy }) {
    const [f, setF] = useState({ ...initial, options: [...(initial.options || [])], indices: [...(initial.indices || [])] });
    const isExisting = initial._isExisting;
    const indicesList = useMemo(() => indices || [], [indices]);
    const hasOptions = f.type === "single_choice" || f.type === "multi_choice";

    const toggleIndex = (id) => {
        setF((cur) => ({
            ...cur, indices: cur.indices.includes(id) ? cur.indices.filter((x) => x !== id) : [...cur.indices, id],
        }));
    };

    return (
        <form onSubmit={(e) => { e.preventDefault(); onSubmit(f); }} className="border border-[#e4e4e7] p-5 bg-[#fdfcf9] space-y-3" data-testid="question-form">
            <div className="flex items-center justify-between">
                <div className="label-caps">{isExisting ? "Editar pergunta" : "Nova pergunta"}</div>
                <button type="button" onClick={onCancel} className="text-[#52525b]"><X size={16} /></button>
            </div>
            <div>
                <label className="label-caps text-xs">Texto da pergunta*</label>
                <textarea className="input-clean" rows={2} value={f.text} onChange={(e) => setF({ ...f, text: e.target.value })} required data-testid="q-text-input" />
            </div>
            <div className="grid sm:grid-cols-4 gap-3">
                <div>
                    <label className="label-caps text-xs">Tipo</label>
                    <select className="input-clean" value={f.type} onChange={(e) => setF({ ...f, type: e.target.value })} data-testid="q-type-input">
                        {QTYPES.map((t) => <option key={t.v} value={t.v}>{t.label}</option>)}
                    </select>
                </div>
                <div>
                    <label className="label-caps text-xs">Grupo</label>
                    <select className="input-clean" value={f.group} onChange={(e) => setF({ ...f, group: e.target.value })} data-testid="q-group-input">
                        <option value="fib">FIB</option>
                        <option value="whr">WHR</option>
                        <option value="global">Geral</option>
                        <option value="custom">Personalizado</option>
                    </select>
                </div>
                <div>
                    <label className="label-caps text-xs">Escopo</label>
                    <select className="input-clean" value={f.scope} onChange={(e) => setF({ ...f, scope: e.target.value })} data-testid="q-scope-input">
                        <option value="individual">Individual</option>
                        <option value="collective">Coletivo</option>
                    </select>
                </div>
                <div>
                    <label className="label-caps text-xs">Ordem</label>
                    <input type="number" className="input-clean" value={f.order} onChange={(e) => setF({ ...f, order: Number(e.target.value) })} data-testid="q-order-input" />
                </div>
            </div>
            <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 text-sm text-[#52525b]">
                    <input type="checkbox" checked={!!f.mandatory} onChange={(e) => setF({ ...f, mandatory: e.target.checked })} data-testid="q-mandatory-input" /> Obrigatória
                </label>
                <label className="flex items-center gap-2 text-sm text-[#52525b]">
                    <input type="checkbox" checked={f.active !== false} onChange={(e) => setF({ ...f, active: e.target.checked })} data-testid="q-active-input" /> Ativa
                </label>
            </div>

            {hasOptions && (
                <div>
                    <label className="label-caps text-xs">Opções (uma por linha)</label>
                    <textarea className="input-clean" rows={4} value={(f.options || []).join("\n")}
                        onChange={(e) => setF({ ...f, options: e.target.value.split("\n").map((s) => s.trim()).filter(Boolean) })}
                        data-testid="q-options-input" />
                </div>
            )}

            <div>
                <label className="label-caps text-xs">Índices ligados</label>
                <div className="flex flex-wrap gap-2 mt-2 max-h-40 overflow-auto p-2 border border-[#e4e4e7] bg-white">
                    {indicesList.map((i) => (
                        <label key={i.id} className="flex items-center gap-1.5 text-xs cursor-pointer border border-[#e4e4e7] px-2 py-1 hover:bg-[#f5f3ee]">
                            <input type="checkbox" checked={f.indices.includes(i.id)} onChange={() => toggleIndex(i.id)} data-testid={`q-idx-${i.id}`} />
                            {i.name}
                        </label>
                    ))}
                </div>
            </div>

            <div className="flex justify-end gap-2">
                <button type="button" onClick={onCancel} className="btn-outline">Cancelar</button>
                <button type="submit" disabled={busy} className="btn-primary" data-testid="q-save-btn">
                    <Save size={14} /> {busy ? "A guardar..." : "Guardar"}
                </button>
            </div>
        </form>
    );
}
